import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {NGXLogger} from 'ngx-logger';
import {MatSnackBar} from '@angular/material/snack-bar';
import {catchError, concatMap, map, of, switchMap, tap} from 'rxjs';
import {MappedHttpErrorResponse, mappedHttpErrorResponseOperator} from '@adnova/jf-ng-components';
import {NewsEntitiesActions} from '../action/news-entities.actions';
import {
  Configuration as AgrarinformationenConfiguration,
  NewsDTO,
  NewsService,
  PageableDTO
} from '../../openapi/agrarinformationen-openapi';
import {NewsDtoExtended} from '../../modules/interfaces/news-extended.interface';
import {ActionCreator, Store} from '@ngrx/store';
import {AppState} from '../states/app.state';
import {TypedAction} from '@ngrx/store/src/models';


@Injectable()
export class NewsEntitiesEffects {
  private _logger = inject(NGXLogger);
  private _newsService = inject(NewsService);
  private _actions$ = inject(Actions);
  private _snackbar = inject(MatSnackBar);
  private _store = inject(Store<AppState>);
  private _agrarConfig = inject(AgrarinformationenConfiguration);

  /**
   * Der Effekt erlaubt asynchrones Laden von Newsn durch serverseitige Datenanfragen.
   * Dabei steuert er die Auslösung von Actions basierend auf dem Erfolg oder Misserfolg der Anfrage.
   *
   * @property onAction - Action Creator, der ausgeführt wird, um den Effekt zu starten
   * @property successAction - Action Creator, der ausgeführt wird, wenn die Daten erfolgreich vom Server abgerufen wurden
   * @property failureAction - Action Creator, der ausgeführt wird, wenn beim Abrufen der Daten ein Fehler aufgetreten ist
   */
  readonly readNews$ = (
    onAction: ActionCreator<any, (props: {
      pageableDto?: PageableDTO,
    }) => ({
      pageableDto?: PageableDTO,
    } & TypedAction<any>)>,
    successAction?: ActionCreator<any, (props: { newsDtos: NewsDTO[], totalElements: number }) => ({
      newsDtos: NewsDTO[],
    } & TypedAction<any>)>,
    failureAction?: ActionCreator<any, (props: { error: MappedHttpErrorResponse, }) => ({
      error: MappedHttpErrorResponse,
    } & TypedAction<any>)>,
  ) => createEffect(
    () => this._actions$.pipe(
      ofType(onAction),
      switchMap(({pageableDto}) => {
        return this._newsService.getNews(
          pageableDto,
        ).pipe(
          switchMap(newsPageDto => {
            this._logger.debug('read news succeeded.');

            const apiBaseUrl = this._agrarConfig.basePath;
            const newsDtos: NewsDtoExtended[] = newsPageDto.content.map(newsDto => {
              return {
                ...newsDto,
                imgUrl: `${apiBaseUrl}/api/v1/news/${newsDto.id}/image`
              } as NewsDtoExtended;
            });
            const pageIndex: number = pageableDto?.number ?? 0;
            const totalElements: number = newsPageDto.totalElements;

            return [
              NewsEntitiesActions.readNewsSuccess({
                newsDtos,
                pageIndex,
                totalElements,
              }),
              ...(successAction ? [successAction({
                newsDtos: newsPageDto.content,
                totalElements: newsPageDto.totalElements,
              })] : []),
            ];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            switchMap(error => {
              this._logger.error('read news failed.', 'error:', error);

              return [
                NewsEntitiesActions.readNewsFailure({error}),
                ...(failureAction ? [failureAction({error})] : []),
              ];
            }),
          )),
        );
      }),
    )
  );

  readonly readNewsFailure$ = createEffect(
    () => this._actions$.pipe(
      ofType(NewsEntitiesActions.readNewsFailure),
      map(({error}) => {
        let errorMsg = '';

        switch (error.status) {
          case 400: {
            errorMsg = 'Die Anfrage war ungültig. Bitte überprüfe deine Eingaben oder versuche es später erneut.';
            break;
          }
          case 403: {
            errorMsg = 'Fehlende Berechtigung für das Laden der News. Bitte kontaktiere deinen Administrator.';
            break;
          }
          case 503: {
            errorMsg = 'Der News-Service ist derzeit nicht verfügbar. Bitte versuche es später erneut.';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Laden der News. Bitte probiere es später erneut.';
          }
        }

        this._snackbar.open(
          errorMsg,
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );
      }),
    ),
    {dispatch: false}
  );

  /**
   * Effekt zum Laden eines News-Artikels anhand der ID.
   */
  readonly getNewsById$ = createEffect(
    () => this._actions$.pipe(
      ofType(NewsEntitiesActions.getNewsById),
      concatMap(({newsId}) => {
        return this._newsService.getNewsById(newsId).pipe(
          map((newsDto) => {
            this._logger.debug(
              'read news by ID succeeded. newsDto:',
              newsDto,
            );

            const apiBaseUrl = this._agrarConfig.basePath;
            const kundeDtoExtended: NewsDtoExtended = {
              ...newsDto,
              imgUrl: `${apiBaseUrl}/api/v1/news/${newsDto.id}/image`
            };

            return NewsEntitiesActions.getNewsByIdSuccess({newsDto: kundeDtoExtended});
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(error => {
              this._logger.error(
                'read news by ID failed. newsId:',
                newsId,
                'error:',
                error,
              );

              return NewsEntitiesActions.getNewsByIdFailure({
                error,
              });
            }),
          )),
        );
      }),
    ),
  );

}
